import dynamic from 'next/dynamic';
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { FilePlus, Search, Upload } from 'lucide-react';
import { type FileFilterState, filePath, folderOf, isLifeTimelineFolder, isTranscriptsPath, nameOf, pathSegments, writeDirectoryOf, type BrowsableFile, folderPathTitle } from '../../../lib/file-browser';
import { creatorTitleResolver, DEFAULT_FILE_SORT, sortFiles, type FileSort } from '../../../lib/files-sort';
import { useFilesBrowser } from '../../../lib/use-files-browser';
import { useAgentName } from '../../../lib/use-agent-name';
import { FILES_FOLD_BREAKPOINT_PX, useContainerNarrow } from '../../../lib/use-container-narrow';
import { useTranslation } from '../../../lib/i18n';
import { Button, Card, Input, OverflowMenu, Toast, type OverflowMenuAction } from '../ui';
import { useFileUpload } from '../../../lib/use-file-upload';
import { uploadFileName } from '../../../lib/file-draft';
import { openFileDraft } from '../../../lib/doc-viewer-presenter';
import type { WorkspaceFile } from '../../../lib/workspace-files';
import { EmptyState } from '../agents/EmptyState';
import { FilesFilterBar } from './FilesFilterBar';
import { FileCreatorRosterProvider } from '../FileCreatedBy';
import { FilesBreadcrumb } from './FilesFolderNav';
import { FilesTable } from './FilesTable';
import { FilesGrid } from './FilesGrid';
import { FilesViewToggle } from './FilesViewToggle';
import { FolderPickerDialog } from './FolderPickerDialog';
import { ShareFileDialog } from './ShareFileDialog';
import { FileAccessDialog } from './FileAccessDialog';

import { offerShareToGroup } from '../../../lib/group-files';
import { useGroupChatEnabled } from '../../../lib/group-chat-flag';
import { ForgetMemoryDialog } from './ForgetMemoryDialog';
import { UploadDestinationDialog } from './UploadDestinationDialog';
import { UploadDraftDialog } from './UploadDraftDialog';
import type { FilesRow } from './files-row';
import { CategoryHeader } from '../category-view/CategoryView';
import styles from './FilesBrowser.module.css';

// Loaded on use: the group-files UI brings its viewer (markdown) and data layer, which this host never needs until then.
const ShareToGroupDialog = dynamic(() => import('../group-chat/GroupFiles').then((m) => m.ShareToGroupDialog), { ssr: false });

// THE Files surface. One component, rendered by both the cross-workspace Files page and a workspace's own
// gallery — because they are the same list with a different ROOT, and every time they were two components they
// drifted: different columns, different sorting, one with the workspace name and one without, one with a
// separate phone layout. "Workspace files and the global files screen are not the same" was the report; this is
// the structural answer to it.
//
// What the enclosing page still owns: its hero, its data, and how a row addresses a file (`rowFor`). Everything
// else — search, filters, folders, sorting, the list/thumbnails switch, share and move — is here.
//
// It carries NO heading of its own. The page above it already says "Files" in its hero, and a second "Files"
// inside the card was the same word twice on one screen.

export interface FilesBrowserProps<T extends BrowsableFile> {
  items: T[];
  /** `''` for the cross-workspace page — which opens FLAT, with the Space as a chip — or a workspace id for
   *  that workspace's own gallery. A picked Space becomes the browsing root (`browseRoot`). */
  root: string;
  /** Resolves the per-surface half of a row: its key, link and caption. See `FilesRow`. */
  rowFor: (item: T, context: { narrowed: boolean; workspaceNames: Record<string, string> }) => FilesRow;
  /** Builds the row's overflow actions — the surface owns them because it owns the BI and the callbacks.
   *
   *  `onGrantAccess` is handed over ONLY on a narrow PANE (see `narrowPane` in the body): the wide row says
   *  grant-access with its share glyph, a narrow row renders no glyph, so only the narrow menu carries the
   *  entry. A surface passes the handler straight through to `buildFileActions` and asks no width question
   *  of its own. */
  actionsFor: (
    row: FilesRow,
    handlers: { onMove: () => void; onSendCopy: () => void; onGrantAccess?: () => void; onShareToGroup?: () => void; onDelete: () => void },
  ) => OverflowMenuAction[][];
  onOpen: (row: FilesRow) => void;
  onRowMenu?: (row: FilesRow) => void;
  /** Reports filter/view/folder interactions for BI. */
  onBi?: (component: string) => void;
  /** Workspace id → display name, so a folder row and a caption never show a raw id. */
  workspaceNames?: Record<string, string>;
  /** Shown when the account genuinely has no files (as opposed to none matching). */
  emptyCaption: string;
  /** Replaces the control band and the empty card when the account has no files — the home's designed empty
   *  state (Figma 21849:55779), which brings its own way to create one. */
  emptyState?: ReactNode;
  selectedName?: string;
  /**
   * Offers "Add" — upload a file you already have, or author a new one — and opens the created file.
   *
   * Opt-in per surface, because not every Files surface has somewhere to put a file: a chat's attachment
   * list is a view of one conversation's uploads, with no folder and no workspace path to write to.
   */
  onCreated?: (file: WorkspaceFile) => void;
  /** The category header's title and description (Figma 21055:251370), drawn above the band with the
   *  Space / agent filters at its end. The home's Files category passes both; the standalone page passes
   *  neither, because its PageHeader already names the page, and gets the filters alone. */
  title?: string;
  description?: ReactNode;
  /** What the breadcrumb's first crumb is called. A surface rooted somewhere other than the top of the
   *  path space names its root — see `FilesBreadcrumb.rootLabel`. */
  rootLabel?: string;
  /**
   * Filters this surface starts with — a Space, or a subtree scope — INSTEAD of a hard root.
   *
   * A root is invisible and inescapable: the breadcrumb slices it off, so there is no path to read and no
   * way out of it. A pre-applied filter is a chip the reader can see and dismiss, and dismissing it shows
   * the whole tree in place. Same component, same breadcrumb, one dismissible question — which is what
   * makes "every surface is the same explorer" true in behaviour and not only in which import it uses.
   */
  initialFilters?: Partial<FileFilterState>;
  /** What the subtree-scope chip is called — see `FilesFilterBar.scopeLabel`. */
  scopeLabel?: string;
  /** The BI page_name a file opened from here reports under — including the draft the `+` opens. */
  pageName: string;
  /** `false` when the host owns the URL: folder and filters stay in memory (see `useFilesBrowser`). */
  stateInUrl?: boolean;
  /**
   * PICK mode (opt-in): the explorer becomes a chooser, with a footer button that picks the folder being browsed
   * (disabled at the root, which is not a folder). A FILE is picked through `onOpen`, so a picker host passes an
   * `onOpen` that picks and an `actionsFor` that offers nothing, and no `onCreated`. Used by a group Maestro's
   * "Read these files and folders" / "Edit these documents" grants — the SAME explorer, never a second list
   * (repo CLAUDE.md, "every place that shows files is THE SAME file explorer").
   */
  pickFolder?: { label: string; onPick: (folder: string) => void };
}

export function FilesBrowser<T extends BrowsableFile>({
  items,
  root,
  rowFor,
  actionsFor,
  onOpen,
  onRowMenu,
  onBi,
  workspaceNames = {},
  emptyCaption,
  emptyState,
  selectedName,
  onCreated,
  rootLabel,
  initialFilters,
  scopeLabel,
  pageName,
  stateInUrl = true,
  title,
  description,
  pickFolder,
}: FilesBrowserProps<T>) {
  const { t } = useTranslation();
  const agent = useAgentName();
  // ═══ THE ONE PLACE THE WIDTH QUESTION IS ASKED, AND THE ONE BOX IT IS ASKED ABOUT ═══════════════
  //
  // `narrowPane` is the single boolean behind all THREE of the row's width-dependent decisions:
  //
  //   1. whether the row/tile renders its dedicated share GLYPH   (`FilesTable` / `FilesGrid`)
  //   2. whether the row menu carries the grant-access ITEM       (`onGrantAccess`, below)
  //   3. whether that menu opens as a bottom SHEET or a popover   (`FileRowMenu`)
  //
  // ⚠️ THE BUG THIS CLOSES. Those three used to ask two different questions of two different boxes:
  // the glyph was hidden by `@container files (max-width: 600px)` — this PANE — while the item and
  // the presentation were decided by `useNarrow()` — the VIEWPORT, at 720. So a viewport of 1440 with
  // the rail plus a docked chat (a normal Symphony configuration, pane ~590) hid the glyph AND
  // withheld the item: no way to grant access at all, on a desktop window. The mirror corner —
  // viewport 600–719 — drew the glyph AND listed the item, which is merely sloppy. One question
  // cannot produce either.
  //
  // It is measured off `.browser`, the element that CARRIES `container-type: inline-size;
  // container-name: files` — so the JS and the stylesheets are not merely using the same number, they
  // are measuring the same box. `useContainerNarrow` is the home's hook, at `FILES_FOLD_BREAKPOINT_PX`
  // = the 600 every `@container files` query on this screen states (see the constant's doc for why the
  // two literals cannot be collapsed, and which test keeps them equal).
  //
  // The pre-measure default is `false`, i.e. WIDE — `useNarrow`'s own default, and the reason it is
  // safe here: the un-measured render is a coherent wide row (glyph present, item absent), never a row
  // with neither. One boolean is what makes that true; two could always disagree.
  //
  // The DESIGN is unchanged by this: wide keeps the glyph and no item, narrow keeps no glyph and the
  // item (the phones' documented exception — iOS `DocumentViewer.swift:573`, Android
  // `FileActionMenu.kt:200`, both last-in-primary). Only the number of questions changed.
  const paneRef = useRef<HTMLDivElement>(null);
  const narrowPane = useContainerNarrow(paneRef, FILES_FOLD_BREAKPOINT_PX);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<FileSort>(DEFAULT_FILE_SORT);
  const [moving, setMoving] = useState<FilesRow | null>(null);
  // The row whose "Send a copy" (link / mailto / download) sheet is open. NOT the same thing as
  // `granting` below — that one changes who may read the file, this one hands out a copy. It is reached
  // from the row MENU at every width; the redesign (§7) took grant-access out of the WIDE menu, not this.
  const [sharing, setSharing] = useState<FilesRow | null>(null);
  // The row whose "who can read this" dialog is open. Reached TWO ways, one per width, never both at once:
  // the row's dedicated share GLYPH on a wide row (§7's affordance), and the bottom sheet's own
  // grant-access item on a narrow one, where that glyph is not rendered. See `narrowPane` above.
  const [granting, setGranting] = useState<FilesRow | null>(null);
  const [sharingToGroup, setSharingToGroup] = useState<FilesRow | null>(null);
  const groupChatEnabled = useGroupChatEnabled();
  // The MEMORY row whose "Forget this?" confirm is open, and the toast that says it is gone. Delete is the
  // one destructive action in here, offered for memory only (`buildFileActions` asks the row's path, so a
  // workspace file never gets it however this callback is wired) — see `ForgetMemoryDialog`.
  const [forgetting, setForgetting] = useState<FilesRow | null>(null);
  const [forgotten, setForgotten] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Depth of dragenter/dragleave pairs. A single boolean flickers: dragging across a child fires
  // `dragleave` on the parent before `dragenter` on the child, so the highlight drops and returns at every
  // internal boundary. Counting keeps it on until the pointer really leaves.
  const [dragDepth, setDragDepth] = useState(0);
  const [uploadReport, setUploadReport] = useState<{ ok: number; failed: string[] } | null>(null);
  /** Files chosen while no destination folder was in view — held until the dialog answers. */
  const [pendingUpload, setPendingUpload] = useState<File[] | null>(null);
  const { state: uploadState, progress, upload } = useFileUpload();

  const browser = useFilesBrowser(items, root, query, initialFilters, { inUrl: stateInUrl, filtersInUrl: false });

  // Data-gated columns: each shows only when at least one file in the current FOLDER carries that datum, so a
  // list of only pre-attribution files hides the column rather than printing a wall of em-dashes. Gated on the
  // folder's whole subtree, not the filtered rows, so a column doesn't flicker as you narrow or type.
  //
  // …and gated FIRST on there being a file row at all. A listing of folders alone — which is what the merged
  // root is for an account that keeps everything filed — otherwise draws four columns no row on screen can
  // fill, under a header promising a writer, a date, a type and a size for things that have none.
  const showFileColumns = browser.files.length > 0;
  const showCreatedBy = showFileColumns && browser.scope.some((item) => item.builtBy);
  const showDate = showFileColumns && browser.scope.some((item) => item.updatedDate);

  // Created-by sorts by the NAME you can read, not the raw actor enum behind it.
  const creatorTitle = useMemo(() => creatorTitleResolver(t, agent.short), [t, agent.short]);
  const rows = useMemo(
    () => sortFiles(browser.files, sort, creatorTitle).map((item) => rowFor(item, { narrowed: browser.narrowed, workspaceNames })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [browser.files, browser.narrowed, sort, creatorTitle, workspaceNames],
  );

  // On the MERGED tree — the cross-workspace surface, browsing from '' rather than from a picked Space —
  // a row's own name never says which Space it is in: two Spaces can each have an "images", and a folder
  // you have walked into still holds files from one particular Space. So the Space is named at every
  // level of that tree, not only at its root.
  //
  // This is the other half of dropping the Space from the breadcrumb (§13): the trail reads
  // `All Files › Folder`, and the Space keeps being said by the Workspace COLUMN (list) / the space badge
  // (grid) inside the folder. Gating this on `folder === ''` is what used to make the Space disappear from
  // the rows at the exact moment the crumb stopped saying it too.
  const mergedRoot = browser.root === '';

  // Offered wherever the surface opts in — including the top of the cross-workspace tree, whose rows are
  // Spaces rather than a folder. There the destination is ASKED for (see `runUpload`) rather than the
  // action being hidden: a menu item that silently disappears reads as broken, and dropping files onto a
  // list that ignores them reads as worse. Both were reported.
  // Except under `transcripts/`, whose files Maestro writes: an upload or a new file there would
  // have no directory to land in and fall through to a Space's.
  // Nor in the life timeline's folders (timeline P1.11): their rows are virtual, and nothing writes there.
  const canUpload = !!onCreated && !isTranscriptsPath(browser.folder) && !isLifeTimelineFolder(browser.folder);
  const dragging = dragDepth > 0;

  /** The names already in this folder, so a collision is numbered rather than refused. */
  const namesHere = useMemo(() => browser.files.map((file) => nameOf(file.name)), [browser.files]);

  /** The destination, spelled for a reader — the Space's name, then any folders under it. */
  const folderLabel = useMemo(() => {
    const segments = pathSegments(browser.folder);
    return [workspaceNames[segments[0]] ?? segments[0], ...segments.slice(1)].join(' / ');
  }, [browser.folder, workspaceNames]);

  const uploadInto = async (
    files: File[],
    folder: string,
    existing: string[],
    /** A single file the user renamed and described in the draft dialog. Absent for a batch. */
    draft?: { name: string; hint: string },
  ) => {
    // The destination decides the directory — see `writeDirectoryOf`. One browser now shows Spaces,
    // agents and skills in the same tree, so a browser-wide answer would put an upload dropped on an
    // agent's folder into `workspaces`.
    const outcomes = await upload(files, folder, existing, writeDirectoryOf(folder) ?? undefined, draft);
    const failed = outcomes.filter((outcome) => !outcome.ok);
    setUploadReport({ ok: outcomes.length - failed.length, failed: failed.map((outcome) => outcome.name) });
  };

  const runUpload = async (files: File[]) => {
    if (!canUpload || files.length === 0) return;
    // A SINGLE file is always filed deliberately — the draft dialog names it, describes it and confirms
    // where it goes, defaulting to the folder in view. A batch goes straight in, except at the top of the
    // tree where there is no folder to go into and the destination has to be asked for.
    if (files.length === 1 || browser.folder === '') {
      setPendingUpload(files);
      return;
    }
    await uploadInto(files, browser.folder, namesHere);
  };

  const nothingAtAll = items.length === 0;
  const designedEmpty = nothingAtAll && !!emptyState;
  const nothingHere = browser.folders.length === 0 && rows.length === 0;
  // The LIST view with something in it is the only state whose rows draw their own card surface at the
  // phone width (see `.cardRows`). An empty state is a paragraph that still needs a card under it —
  // otherwise it is text floating on the page background.
  const rowsAsCards = browser.view === 'list' && !nothingAtAll && !nothingHere;
  // The GRID view drops the card entirely, at EVERY width (see `.cardTiles`): the Figma puts the tiles
  // straight on the page, because each tile is already a card and a card behind a grid of cards is a
  // second surface that says nothing. An empty state still keeps its card, same as above.
  const tilesOnPage = browser.view === 'thumbnails' && !nothingAtAll && !nothingHere;

  return (
    <FileCreatorRosterProvider>
      {/* ONE SIZE CONTAINER FOR THE WHOLE BROWSER, and ONE threshold inside it.
          `container-name: files` + `@container files (max-width: 600px)` is the single question every
          part of this screen folds on — the control band, the table's rows, the folder rows and the
          thumbnail grid. It replaced a `@media (max-width: 720px)` on the band and a
          `@container (max-width: 460px)` on the rows, which is exactly two questions asked at two
          thresholds: everything between them was a half-converted screen, and at ~591px the phone's
          filter band rendered on top of the desktop table.

          A CONTAINER is the right question (the pane is already narrowed by the rail, the docked chat
          and the side panel, so the viewport was never what decided this layout); the NAME is what
          stops a nested container — `FilesTable`'s own `.table`, which still drops columns one at a
          time off its own width — from capturing the query.

          THIS ELEMENT is also what `narrowPane` measures (`paneRef` above). The row's three
          width-dependent decisions — the share glyph, the grant-access menu item, sheet-vs-popover —
          are one boolean read off the same box these queries are asked about, so no part of the row
          can fold while another part is still unfolded. */}
      <div ref={paneRef} className={styles.browser}>
      {/* Which Space and which agent: the widest questions, so they sit with the page's own header rather
          than in the band (Figma 21055:251370). The band keeps the ones that shape the list. */}
      <CategoryHeader
        title={title}
        description={designedEmpty ? undefined : description}
        tight={designedEmpty}
        filters={
          nothingAtAll ? undefined : (
            <FilesFilterBar
              axes="scope"
              items={browser.scope}
              workspaceItems={items}
              workspaceNames={workspaceNames}
              filters={browser.filters}
              scopeLabel={scopeLabel}
              onChange={browser.setFilters}
              onBi={onBi}
            />
          )
        }
      />
      {/* The whole control row is a CARD now, and the breadcrumb rides on its leading edge (Figma — the
          redesign's §3): where you are and how you are narrowing are one band of chrome above the list,
          rather than a bare flex row on the page over a crumb tucked inside the list's own card.

          It is a DS `Card` (sunken, size S → the design's radius 16) with its own 16·20 padding rather
          than a hand-rolled surface — `test/card-spec.test.ts` exists precisely to stop the second kind.

          Search and the view switch ride with the chips on the trailing edge, because they are the same
          job — narrowing and shaping the list. */}
      {!designedEmpty && (
      <Card variant="sunken" size="sm" disablePadding className={styles.head}>
        {/* Against the browser's OWN root, not the prop: on the cross-workspace surface a picked Space is the
            root, and the crumb has to start there rather than offering a way back to a level that no longer
            exists. `hideRootWorkspace` drops the SPACE step on the merged tree (§13) — the trail reads
            `All Files › Folder`, and which Space you are looking at is said by the Workspace column / the
            grid's space badge instead of by a crumb that cannot be navigated back to anyway. */}
        <FilesBreadcrumb
          rootLabel={rootLabel}
          className={styles.crumbs}
          folder={browser.folder}
          root={browser.root}
          hideRootWorkspace={browser.root === ''}
          /* …and at the root it says "All Files" rather than rendering nothing. The band's leading edge
             is where you-are-here lives; an empty one let the chips slide across into it, so the same
             screen had its controls in two different places depending on the folder. */
          showRootWhenEmpty
          onNavigate={browser.setFolder}
          workspaceNames={workspaceNames}
        />
        <div className={styles.headControls}>
          {/* Narrowing controls have nothing to narrow when the account owns no files at all — a filter
              bar, a search field and a grid/rows switch over zero rows are furniture, not affordances.
              Only Add survives, because it is the one control that gets the reader OUT of empty. */}
          {!nothingAtAll && (
            <>
              <FilesFilterBar
                axes="shape"
                className={styles.filters}
                items={browser.scope}
                workspaceItems={items}
                workspaceNames={workspaceNames}
                filters={browser.filters}
                onChange={browser.setFilters}
                onBi={onBi}
              />
              <Input
                className={styles.search}
                size="medium"
                type="search"
                startIcon={<Search className={styles.searchIcon} size={16} aria-hidden />}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onClear={() => setQuery('')}
                clearAriaLabel={t('search.dialog.clear')}
                placeholder={t('agentCreations.search')}
                aria-label={t('agentCreations.search')}
              />
              <FilesViewToggle
                className={styles.viewToggle}
                view={browser.view}
                onChange={(view) => {
                  onBi?.(`files_view_${view}`);
                  browser.setView(view);
                }}
              />
            </>
          )}
          {/* Adding a file goes in the head row rather than the page's hero, because this row is the one
              that knows which folder you are looking at — and that folder is where a file lands. LAST,
              after the three controls that NARROW the list: adding is a different kind of act.

              A MENU rather than a button, because there are two ways to add a file and neither is the
              obvious default — uploading one you already have, or authoring a new one here. */}
          {canUpload && (
            <OverflowMenu
              ariaLabel={t('files.add.action')}
              label={t('files.add.action')}
              triggerVariant="contained"
              triggerClassName={styles.create}
              align="end"
              actions={[
                {
                  key: 'upload',
                  label: t('files.upload.action'),
                  icon: <Upload size={16} aria-hidden />,
                  onSelect: () => {
                    onBi?.('files_upload');
                    fileInputRef.current?.click();
                  },
                },
                {
                  key: 'new',
                  label: t('files.new.action'),
                  icon: <FilePlus size={16} aria-hidden />,
                  onSelect: () => {
                    onBi?.('files_new');
                    // Straight into the editor, holding a draft. It used to open a small dialog for the
                    // name, type and folder, write an EMPTY file, and only then open this — two modals for
                    // one act, the second landing on the blank document the first had just made.
                    openFileDraft({ folder: browser.folder, pageName, directory: writeDirectoryOf(browser.folder) ?? undefined });
                  },
                },
              ]}
            />
          )}
        </div>
      </Card>
      )}

      {/* The list's own surface. It goes flat at the SAME 600 the band and the rows fold at — the rows
          become cards of their own there (see `FilesTable.module.css`), and a card behind a column of
          cards is either a card inside a card or a flat list floating on the page, depending on which
          of the two rules fired. One container, one threshold, so they cannot disagree. */}
      {/* The whole list is the drop target, because that is what a person aims at. The handlers sit on a
          plain wrapper rather than the Card so the Card keeps its own semantics, and `dragOver` must
          preventDefault or the browser opens the file instead of letting us have it. */}
      <div
        className={dragging ? `${styles.dropZone} ${styles.dropping}` : styles.dropZone}
        onDragEnter={canUpload ? (e) => { if (hasFiles(e)) { e.preventDefault(); setDragDepth((d) => d + 1); } } : undefined}
        onDragOver={canUpload ? (e) => { if (hasFiles(e)) e.preventDefault(); } : undefined}
        onDragLeave={canUpload ? () => setDragDepth((d) => Math.max(0, d - 1)) : undefined}
        onDrop={
          canUpload
            ? (e) => {
                if (!hasFiles(e)) return;
                e.preventDefault();
                setDragDepth(0);
                onBi?.('files_drop');
                void runUpload([...e.dataTransfer.files]);
              }
            : undefined
        }
      >
        {dragging && (
          <div className={styles.dropOverlay} aria-hidden>
            <Upload size={20} />
            {/* At the top of the tree there is no folder to name yet — say what will happen instead of
                interpolating an empty one. */}
            <span>{browser.folder === '' ? t('files.upload.dropAnywhere') : t('files.upload.drop', { folder: folderLabel })}</span>
          </div>
        )}

        {/* Never shown — the menu's Upload item clicks it. `multiple` because dropping a handful of files
            and picking a handful should behave the same way. */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className={styles.fileInput}
          onChange={(e) => {
            const picked = [...(e.target.files ?? [])];
            // Reset first, so picking the SAME file again still fires a change event.
            e.target.value = '';
            void runUpload(picked);
          }}
        />

        {(uploadState === 'uploading' || uploadReport) && (
          <div className={styles.uploadStatus} role="status">
            {uploadState === 'uploading'
              ? t('files.upload.progress', { done: progress?.done ?? 0, total: progress?.total ?? 0 })
              : uploadReport?.failed.length
                ? t('files.upload.someFailed', { count: uploadReport.failed.length, names: uploadReport.failed.join(', ') })
                : t('files.upload.done', { count: uploadReport?.ok ?? 0 })}
          </div>
        )}

      {designedEmpty ? (
        emptyState
      ) : nothingAtAll ? (
        // The illustrated card the rest of the app uses for "nothing here yet" (Figma `11246:167061`) —
        // never the bare `.noResults` paragraph, which is reserved for "nothing MATCHES", a state that
        // still has filters to clear. Default glyph (card-stack), matching the Apps screen's empty
        // state rather than the narrower per-workspace Files TILE's own "files" glyph.
        <EmptyState label={emptyCaption} />
      ) : (
      <Card
        disablePadding
        className={[styles.card, rowsAsCards && styles.cardRows, tilesOnPage && styles.cardTiles].filter(Boolean).join(' ')}
      >
          {nothingHere ? (
            // "Nothing here" and "nothing matches what you asked for" are different things to tell someone —
            // the second one has an action attached (clear the filters).
            <div className={styles.noResults}>{browser.narrowed ? t('files.filter.noMatch') : t('agentCreations.noResults')}</div>
          ) : browser.view === 'thumbnails' ? (
            <FilesGrid
              rows={rows}
              /* The glyph's gate and the menu item's gate, from ONE boolean — see `narrowPane`. */
              narrowPane={narrowPane}
              folders={browser.folders}
              workspaceNames={workspaceNames}
              showFolderWorkspace={mergedRoot}
              onOpenFolder={(path) => {
                onBi?.('open_folder');
                browser.setFolder(path);
              }}
              onOpen={onOpen}
              onShare={setGranting}
              rowActions={!pickFolder}
              onRowMenu={onRowMenu}
              selectedName={selectedName}
              actionsFor={(row) =>
                actionsFor(row, {
                  onMove: () => setMoving(row),
                  onSendCopy: () => setSharing(row),
                  // Narrow only — the tile renders no share glyph there, so the sheet is the only place
                  // left to say it. SAME boolean that suppresses the glyph. See `narrowPane` above.
                  onGrantAccess: narrowPane ? () => setGranting(row) : undefined,
                  // Group chat Phase 7 — offered only with the flag on and a file id to grant against.
                  onShareToGroup: offerShareToGroup(row.item, groupChatEnabled) ? () => setSharingToGroup(row) : undefined,
                  onDelete: () => setForgetting(row),
                })
              }
            />
          ) : (
            <FilesTable
              rows={rows}
              /* The glyph's gate and the menu item's gate, from ONE boolean — see `narrowPane`. */
              narrowPane={narrowPane}
              folders={browser.folders}
              onOpenFolder={(path) => {
                onBi?.('open_folder');
                browser.setFolder(path);
              }}
              workspaceNames={workspaceNames}
              showFolderWorkspace={mergedRoot}
              showFileColumns={showFileColumns}
              sort={sort}
              onSortChange={(next) => {
                onBi?.(`files_sort_${next.key}`);
                setSort(next);
              }}
              showCreatedBy={showCreatedBy}
              showDate={showDate}
              onOpen={onOpen}
              onShare={setGranting}
              rowActions={!pickFolder}
              onRowMenu={onRowMenu}
              selectedName={selectedName}
              actionsFor={(row) =>
                actionsFor(row, {
                  onMove: () => setMoving(row),
                  onSendCopy: () => setSharing(row),
                  // Narrow only — the row renders no share glyph there, so the sheet is the only place
                  // left to say it. SAME boolean that suppresses the glyph. See `narrowPane` above.
                  onGrantAccess: narrowPane ? () => setGranting(row) : undefined,
                  // Group chat Phase 7 — offered only with the flag on and a file id to grant against.
                  onShareToGroup: offerShareToGroup(row.item, groupChatEnabled) ? () => setSharingToGroup(row) : undefined,
                  onDelete: () => setForgetting(row),
                })
              }
            />
          )}
      </Card>
      )}

      </div>
      {pickFolder && (
        <div className={styles.pickFooter}>
          <Button
            variant="outlined"
            disabled={browser.folder === ''}
            onClick={() => pickFolder.onPick(browser.folder)}
            data-testid="files-pick-folder"
          >
            {pickFolder.label}
          </Button>
        </div>
      )}
      </div>

      {/* ONE file gets the full draft: a preview, and the same name/description/location questions the
          editor asks about a file you author. A BATCH keeps the destination-only ask — naming twelve files
          one dialog at a time is worse than the bulk write the user was reaching for, and there is no one
          preview that stands for all of them. */}
      {pendingUpload?.length === 1 && (
        <UploadDraftDialog
          file={pendingUpload[0]}
          defaultFolder={browser.folder}
          workspaceNames={workspaceNames}
          busy={uploadState === 'uploading'}
          onCancel={() => setPendingUpload(null)}
          onConfirm={({ name, description, folder }) => {
            const file = pendingUpload[0];
            setPendingUpload(null);
            const existing = items.filter((item) => folderOf(filePath(item)) === folder).map((item) => nameOf(item.name));
            // `uploadFileName`, never the AUTHORING rule — see its doc: `draftFileName` would file
            // `invoice.pdf` as `invoice.pdf.md`.
            void uploadInto([file], folder, existing, { name: uploadFileName(name, file.name), hint: description });
          }}
        />
      )}
      {pendingUpload && pendingUpload.length > 1 && (
        <UploadDestinationDialog
          count={pendingUpload.length}
          onCancel={() => setPendingUpload(null)}
          onConfirm={(folder) => {
            const files = pendingUpload;
            setPendingUpload(null);
            const existing = items.filter((item) => folderOf(filePath(item)) === folder).map((item) => nameOf(item.name));
            void uploadInto(files, folder, existing);
          }}
        />
      )}

      {sharingToGroup?.item.fileId && (
        <ShareToGroupDialog file={{ fileId: sharingToGroup.item.fileId, name: sharingToGroup.item.name.split('/').pop() || sharingToGroup.item.name }} onClose={() => setSharingToGroup(null)} />
      )}
      {moving && <FolderPickerDialog item={moving.item} onClose={() => setMoving(null)} />}
      {sharing && <ShareFileDialog item={sharing.item} onClose={() => setSharing(null)} />}
      {/* Every file can be granted. A file older than the id field has none, and the server mints one
          from the path at share time — which is why the path is passed alongside whatever id exists.
          The path is the file's full `workspaces` key, which is how the file system addresses it. */}
      {granting && (
        <FileAccessDialog
          fileId={granting.item.fileId ?? ''}
          filePath={`${granting.item.workspaceId ?? ''}/${granting.item.name}`}
          fileName={granting.item.name}
          onClose={() => setGranting(null)}
        />
      )}
      {/* Forgetting a memory. The dialog asks and deletes; the list re-reads through the query cache
          (`useFileDelete` invalidates the account-wide entry), so the row is gone without anything here
          having to know it was. The toast is the only trace. */}
      {forgetting && (
        <ForgetMemoryDialog
          item={forgetting.item}
          onClose={() => setForgetting(null)}
          onForgotten={() => {
            setForgetting(null);
            setForgotten(true);
          }}
        />
      )}
      <Toast open={forgotten} message={t('files.action.deleted')} severity="success" onClose={() => setForgotten(false)} />
    </FileCreatorRosterProvider>
  );
}

/** The folder a file lives in, spelled for a reader: the workspace's NAME, then any folders under it. Shared by
 *  both surfaces' `rowFor`, so a caption reads the same on either screen. */
export function folderCaption(
  item: BrowsableFile,
  workspaceNames: Record<string, string>,
  t: (key: string) => string,
): string | undefined {
  const segments = pathSegments(folderOf(filePath(item)));
  if (segments.length === 0) return undefined;
  // Every level resolves the way a folder row's does: the first is a Space id or an account root, and
  // neither is printed raw; the fixed folders below them are translated.
  return folderPathTitle(segments.join('/'), workspaceNames, t);
}

/** A row's display name — its last path segment. `name` on the wire is the whole path within the workspace. */
export const rowName = nameOf;

/** Whether a drag carries FILES, as opposed to text or a dragged element from the page itself.
 *
 *  Checked on `dragenter`/`dragover` as well as on the drop: highlighting the list for a dragged selection
 *  of text, and then swallowing that drop, is worse than ignoring it. `types` is the only thing readable
 *  during a drag — `items`/`files` are deliberately empty until the drop, for privacy. */
function hasFiles(event: { dataTransfer: DataTransfer | null }): boolean {
  return !!event.dataTransfer && [...event.dataTransfer.types].includes('Files');
}
